import { motion } from "framer-motion";
import { Seo } from "@/components/site/Seo";
import { LineReveal, SectionHead, FadeUp } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { CtaBand } from "@/components/home/CtaBand";
import { Stats } from "@/components/home/Why";
import { SITE } from "@/data/site";
import { brandItems } from "@/pages/Home";
import { BrandWall } from "@/components/site/BrandWall";

const VALUES = [
  { t: "Quality assurance", d: "Only ISI-marked, BIS-compliant products from authorised channels. No grey-market stock, ever." },
  { t: "Technical guidance", d: "Load calculations, cable sizing, switchgear selection. We share what we know so you buy right." },
  { t: "Complete stock", d: "One stop for the entire job: conduit, wire, switches, boards, fans and accessories." },
  { t: "Transparent GST billing", d: `Proper tax invoices under GSTIN ${SITE.gstin} for every order, big or small.` },
];

const SERVE = [
  { t: "Homeowners", d: "Renovating a flat or fitting out a new home? Pick premium switches and fans with our help." },
  { t: "Electricians & contractors", d: "Trade pricing, bulk stock and fast pick-up so your site work never waits on material." },
  { t: "Interior designers & architects", d: "Designer plates and smart automation to match any interior concept." },
  { t: "Offices, shops & societies", d: "Cabling, protection gear and maintenance supplies for buildings and businesses." },
];

export default function About() {
  return (
    <>
      <Seo path="/about" title="About Rama Electricals | Trusted Electrical Store in Goregaon East"
        description="Learn about Rama Electricals, a trusted electrical shop at Royal Palms, Goregaon East, Mumbai. Genuine products, expert advice and GST billing for homes, electricians and businesses." />
      <section className="relative px-6 pt-40 pb-20" data-testid="about-hero">
        <div className="mx-auto max-w-7xl">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="eyebrow mb-8">About Rama Electricals</motion.p>
          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[42px] sm:text-6xl lg:text-[96px] max-w-6xl">
            <LineReveal onLoad delay={0.15} lines={["A small store with", <span key="a" className="neon-text">a big promise:</span>, <span key="b" className="outline-text">genuine power.</span>]} />
          </h1>
        </div>
      </section>
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-12">
          <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
            className="lg:col-span-7 aspect-[16/10] rounded-[28px] overflow-hidden border border-white/10">
            <img src="/img/hero.jpg" alt="Neon-lit electrical installation, Rama Electricals Mumbai" className="h-full w-full object-cover" />
          </motion.div>
          <div className="lg:col-span-5 space-y-6 text-[#a5b4c8] leading-relaxed text-base md:text-lg">
            <FadeUp><p className="text-white font-display text-2xl leading-snug">Rama Electricals was built on one simple idea: every customer deserves genuine electrical products and honest advice.</p></FadeUp>
            <FadeUp delay={0.1}><p>From our store at MasterMind IV, Royal Palms in Goregaon East, we supply wires and cables, modular switches, energy-efficient fans, MCBs, RCCBs and distribution boards to families, electricians, contractors and businesses across Mumbai.</p></FadeUp>
            <FadeUp delay={0.2}><p>We are primary dealers for Legrand, Polycab and L&K, and we also stock Schneider, KEI, Finolex, Siemens, Selec, Meanwell, Anchor, RR and Precision. We take the time to explain the difference between them, so you pay for what you actually need.</p></FadeUp>
          </div>
        </div>
      </section>
      <Stats />
      <section className="px-6 py-28" data-testid="values-section">
        <div className="mx-auto max-w-7xl">
          <SectionHead eyebrow="Our pillars" title={["Four things we", "never compromise on."]} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v, i) => (
              <FadeUp key={v.t} delay={i * 0.08} className="glass rounded-3xl p-8 min-h-[260px] flex flex-col justify-between transition-colors duration-300 hover:border-[#39FF14]/40">
                <span className="font-mono text-sm text-[#39FF14]">0{i + 1}</span>
                <div><h3 className="font-display text-2xl">{v.t}</h3><p className="mt-3 text-sm text-[#a5b4c8] leading-relaxed">{v.d}</p></div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <section className="py-12 border-y border-white/10"><Marquee items={brandItems} duration={50} reverse itemClass="font-display text-2xl md:text-3xl text-slate-300" sep="●" /></section>
      <BrandWall />
      <section className="px-6 py-28" data-testid="serve-section">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5"><SectionHead eyebrow="Who we serve" title={["Built for everyone", "who builds."]} text="Whether it is one switch or a full building, our team treats every order with the same care." /></div>
          <div className="lg:col-span-7 divide-y divide-white/10 border-y border-white/10">
            {SERVE.map((s, i) => (
              <FadeUp key={s.t} delay={i * 0.06} className="group py-8 grid sm:grid-cols-12 gap-4">
                <span className="sm:col-span-1 font-mono text-xs text-[#39FF14] pt-2">0{i + 1}</span>
                <h3 className="sm:col-span-5 font-display text-2xl transition-colors duration-300 group-hover:text-[#39FF14]">{s.t}</h3>
                <p className="sm:col-span-6 text-[#a5b4c8] text-sm md:text-base leading-relaxed">{s.d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
